import React from 'react';
import { motion } from 'framer-motion';

const IULProjectDetails = ({ project }) => {
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
      {/* Challenges & Highlights - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Project Challenges */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-red-400">🔴 Challenges</h3>
          <div className="space-y-2">
            {project.details.challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{challenge}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Project Highlights */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-green-400">🟢 Highlights</h3>
          <div className="space-y-2">
            {project.details.highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{highlight}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Race Bar Chart */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">⚡ Execution Performance (Race Bar)</h3>
        <div className="space-y-5">
          {/* SPOCK */}
          <div>
            <div className="flex justify-between mb-2">
              <span className="font-bold text-green-400">SPOCK Engine</span>
              <span className="text-gray-300 text-sm">212 items in 6.02s</span>
            </div>
            <div className="relative h-10 bg-gray-700 rounded-lg overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.5, delay: 0.3 }}
                className="h-full bg-gradient-to-r from-green-500 to-emerald-400 flex items-center justify-end pr-4"
              >
                <span className="text-white text-sm font-bold">212 items</span>
              </motion.div>
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold">6.02s</span>
            </div>
            <div className="text-right text-xs text-green-400 mt-1">
              ~0.028s per item
            </div>
          </div>

          {/* Traditional */}
          <div>
            <div className="flex justify-between mb-2">
              <span className="font-bold text-red-400">Traditional Engine</span>
              <span className="text-gray-300 text-sm">20 items in 4.58s</span>
            </div>
            <div className="relative h-10 bg-gray-700 rounded-lg overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(20 / 212) * 100}%` }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-red-500 to-orange-400 flex items-center justify-end pr-4"
              >
                <span className="text-white text-sm font-bold">20 items</span>
              </motion.div>
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold">4.58s</span>
            </div>
            <div className="text-right text-xs text-red-400 mt-1">
              ~0.229s per item
            </div>
          </div>

          {/* Performance Summary */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
            <p className="text-green-400 text-sm font-bold">
              SPOCK is <span className="text-xl">8.2x</span> faster per item
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Processes <span className="text-green-400">10.6x</span> more items in similar time
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Effort Chart */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">⏱️ Configuration Effort Comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SPOCK */}
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-xl p-5">
              <h4 className="font-bold text-green-400 mb-3">SPOCK Engine</h4>
              <div className="relative w-36 h-36 mx-auto mb-3">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="10"
                  />
                  <motion.circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="url(#greenGradient)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: (2 / 12.5) }}
                    transition={{ duration: 1.5, delay: 0.3 }}
                  />
                  <defs>
                    <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10B981" />
                      <stop offset="100%" stopColor="#34D399" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-green-400">2</span>
                  <span className="text-xs text-gray-400">days</span>
                </div>
              </div>
              <p className="text-base font-bold text-gray-200">200+ items</p>
              <p className="text-xs text-green-400 mt-2">
                ~6.25x faster
              </p>
            </div>
          </div>

          {/* Traditional */}
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/40 rounded-xl p-5">
              <h4 className="font-bold text-red-400 mb-3">Traditional Engine</h4>
              <div className="relative w-36 h-36 mx-auto mb-3">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="10"
                  />
                  <motion.circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="url(#redGradient)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                  />
                  <defs>
                    <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#EF4444" />
                      <stop offset="100%" stopColor="#F97316" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-red-400">12.5</span>
                  <span className="text-xs text-gray-400">days</span>
                </div>
              </div>
              <p className="text-base font-bold text-gray-200">20 items</p>
              <p className="text-xs text-red-400 mt-2">
                ~0.625 days per item
              </p>
            </div>
          </div>
        </div>

        {/* Efficiency Summary */}
        <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
          <p className="text-green-400 text-sm font-bold">
            SPOCK configuration is <span className="text-xl">6.25x</span> faster
          </p>
          <p className="text-xs text-gray-400 mt-1">
            SPOCK: 2 days for 200+ items vs Traditional: 12.5 days for 20 items
          </p>
        </div>
      </div>

      {/* Architecture Comparison */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">🏗️ Architecture Transformation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Old Architecture */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold">
                Before: Traditional Architecture
              </span>
            </div>
            <div className="relative rounded-lg overflow-hidden border-2 border-red-500/30 bg-gray-900/50">
              <img
                src="/pic/iul/iul1.webp"
                alt="Traditional Architecture"
                className="w-full h-auto object-contain"
              />
              <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                Complex & Slow
              </div>
            </div>
            <div className="mt-2 text-sm text-red-400">
              ❌ 200+ configuration layers<br/>
              ❌ Depends on complex UI-based forms<br/>
              ❌ Time-consuming
            </div>
          </div>

          {/* New Architecture */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold">
                After: SPOCK Architecture
              </span>
            </div>
            <div className="relative rounded-lg overflow-hidden border-2 border-green-500/30 bg-gray-900/50">
              <img
                src="/pic/iul/iul2.webp"
                alt="SPOCK Architecture"
                className="w-full h-auto object-contain"
              />
              <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                Efficient & Fast
              </div>
            </div>
            <div className="mt-2 text-sm text-green-400">
              ✅ Recursive algorithm<br/>
              ✅ On-demand Elastic Resources<br/>
              ✅ Markdown Configuration<br/>
              ✅ High-performance engine
            </div>
          </div>
        </div>

        {/* Transformation Arrow */}
        <div className="flex justify-center my-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-gradient-to-r from-red-500 via-purple-500 to-green-500 text-white px-6 py-2 rounded-full font-bold flex items-center gap-2"
          >
            <span>Transformation</span>
            <span className="text-xl">→</span>
            <span className="text-green-300">6.25x Faster</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default IULProjectDetails;
